/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ChangeOptions, Change, toArray } from '@typescript-kit/core';
import { Entity, EntityData, EntityFieldKey, EntityModel } from '@typescript-kit/data';

export class ProjectFieldKey extends EntityFieldKey {
  static readonly COMPANY_ID = 'companyId';
  static readonly ID = 'id';
  static readonly PARENT_ID = 'parentId';
  static readonly NAME = 'name';
  static readonly IS_CONFIRMED = 'isConfirmed';
  static readonly IS_ACTIVE = 'isActive';
}

export interface ProjectData extends EntityData {
  companyId?: string;
  id?: string;
  parentId?: string;
  name?: string;
  isConfirmed?: boolean;
  isActive?: boolean;
}

export class Project extends Entity implements ProjectData {
  readonly companyId: string;
  readonly id: string;
  readonly parentId: string;
  readonly name: string;
  readonly isConfirmed: boolean;
  readonly isActive: boolean;

  constructor(data: ProjectData) {
    super(data);
    this.id = data.id;
    this.parentId = data.parentId;
    this.name = data.name;
    this.isConfirmed = data.isConfirmed;
    this.isActive = data.isActive;
  }
}

export class ProjectModel extends EntityModel implements ProjectData {
  static readonly KEYS = EntityModel.KEYS.concat(
    Object.keys(ProjectFieldKey).map(key => ProjectFieldKey[key])
  );

  constructor(data: ProjectData) {
    super(data, ProjectModel.KEYS);
  }

  get companyId(): string {
    return this.get(ProjectFieldKey.COMPANY_ID);
  }

  get id(): string {
    return this.get(ProjectFieldKey.ID);
  }

  set id(value: string) {
    this.set(ProjectFieldKey.ID, value);
  }

  get parentId(): string {
    return this.get(ProjectFieldKey.PARENT_ID);
  }

  set parentId(value: string) {
    this.set(ProjectFieldKey.PARENT_ID, value);
  }

  get name(): string {
    return this.get(ProjectFieldKey.NAME);
  }

  set name(value: string) {
    this.set(ProjectFieldKey.NAME, value);
  }

  get isConfirmed(): boolean {
    return this.get(ProjectFieldKey.IS_CONFIRMED);
  }

  set isConfirmed(value: boolean) {
    this.set(ProjectFieldKey.IS_CONFIRMED, value);
  }

  get isActive(): boolean {
    return this.get(ProjectFieldKey.IS_ACTIVE);
  }

  set isActive(value: boolean) {
    this.set(ProjectFieldKey.IS_ACTIVE, value);
  }

  setValues(values: ProjectData, options?: ChangeOptions): { [key: string]: Change<any>; } {
    return super.setValues(values, options);
  }
}

export interface ProjectTree {
  [key: string]: ProjectTree | null;
};

export function getProjectTree(project: Iterable<Project>, keyName: keyof Project): ProjectTree {
  const projects = toArray(project);
  const topLevelProjects = projects.filter((p) => !p.parentId);

  function getProjectOptions(topLevelProjects: Project[], projects: Project[]): ProjectTree | null {
    const options: ProjectTree = {};

    for (const project of topLevelProjects) {
      const key = project[keyName];

      if (typeof key !== 'string') {
        continue;
      }

      const children = projects.filter((p) => p.parentId === project.id);

      options[key] = children.length === 0 ? null : getProjectOptions(children, projects);
    }

    return options;
  }

  return getProjectOptions(topLevelProjects, projects);
}
